import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../utils/languages';
import { ALL_WEEK_DAYS, UNAVAILABILITY_CONFIG, UI_COLORS, BOOKING_STATUS_CONFIG } from './calendarConstants';
import useCalendarColors from './hooks/useCalendarColors';
import { loadBand, isQuietDay, formatHours, isAllDayAbsence, absenceSpanText } from './hooks/monthUtils';
import { parseDateKey } from './hooks/dateUtils';
import { TooltipRow } from './EventTooltipContent';

const MAX_STATUS_DOTS = 10;
// A single click waits this long for a second one before it opens the peek. Opening the
// peek narrows the grid, and a cell that moves between the two clicks of a double-click
// sends the second click (and the dblclick) to a neighbour; with the wait the layout holds
// still until the browser has decided which of the two it was.
const DOUBLE_CLICK_GRACE_MS = 220;
// Below this the free hours are not worth advertising; the cell shows booked/capacity instead.
const FREE_HOURS_WORTH_SHOWING = 4;

/**
 * Thin utilization bar; a red tick at the right edge marks an overbooked day.
 */
export function LoadBar({ percent, color, height = 6 }) {
  const pct = Math.max(0, Math.min(100, percent || 0));
  return (
    <Box sx={{ height, bgcolor: '#edf2f7', borderRadius: height, overflow: 'hidden', position: 'relative' }}>
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: height, transition: 'width 300ms' }} />
      {percent > 100 && (
        <Box sx={{ position: 'absolute', top: 0, right: 0, width: 3, height: '100%', bgcolor: '#c53030' }} />
      )}
    </Box>
  );
}

/**
 * One dot per work order, coloured by state; overflow collapses into "+N".
 */
export function StatusDots({ byState, getStateConfig, max = MAX_STATUS_DOTS }) {
  const dots = [];
  Object.entries(byState || {}).forEach(([state, count]) => {
    for (let i = 0; i < count; i += 1) dots.push(state);
  });
  const shown = dots.slice(0, max);
  return (
    <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap', alignItems: 'center' }}>
      {shown.map((state, index) => (
        <Box
          key={`${state}-${index}`}
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: getStateConfig(state)?.color || '#a0aec0' }}
        />
      ))}
      {dots.length > max && (
        <Typography component="span" sx={{ fontSize: '10px', color: 'text.disabled', fontWeight: 600 }}>
          +{dots.length - max}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Absence chips: the mechanic's initials in the colour of the absence type.
 */
export function AbsenceChips({ absences, employeesById, compact = false, withTooltips = true }) {
  const { t } = useTranslation();
  if (!absences || absences.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap' }}>
      {absences.map((absence, index) => {
        const config = UNAVAILABILITY_CONFIG[absence.type] || UNAVAILABILITY_CONFIG.personal;
        const employee = employeesById[absence.employee_id];
        // A whole-shift absence is a filled chip, a shorter one (a two-hour errand) an
        // outlined chip: the mechanic is still mostly there.
        const allDay = isAllDayAbsence(absence);
        const span = absenceSpanText(absence);
        // Inside the month cell the day's own hover card names the absences, so the chip
        // stays silent there instead of stacking a second tooltip on top.
        const Wrap = withTooltips ? Tooltip : React.Fragment;
        const wrapProps = withTooltips ? { title: `${employee?.name || ''} · ${t(config.labelKey)}${span ? ` ${span}` : ''}` } : {};
        return (
          <Wrap key={`${absence.employee_id}-${index}`} {...wrapProps}>
            <Box
              component="span"
              data-testid={`month-absence-${absence.employee_id}`}
              data-all-day={allDay ? 'true' : 'false'}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                px: compact ? 0.5 : 0.75,
                py: '1px',
                borderRadius: '5px',
                bgcolor: allDay ? config.bg : 'transparent',
                boxShadow: allDay ? 'none' : `inset 0 0 0 1px ${config.color}`,
                color: config.color,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: 1.5,
              }}
            >
              <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: config.color }} />
              {employee?.initials || '?'}
            </Box>
          </Wrap>
        );
      })}
    </Box>
  );
}

/**
 * The hover card of a day, in the same dress as the week and day views' event tooltips
 * (EventTooltipContent): a bold header, icon rows, sections split by a divider. One row
 * per mechanic with their own room, the mechanics with the most room first and the absent
 * ones last, then the day's exceptions. The day total in the cell cannot say whether a
 * job fits anyone (8 h free may be one free mechanic or five with 1.6 h each); this can,
 * without opening the day. Reads the month payload only.
 */
export function DayHoverCard({ day, employeesById, showParts, showBookings }) {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n?.language);
  const date = parseDateKey(day.date);
  const weekday = ALL_WEEK_DAYS[(date.getDay() + 6) % 7];
  const absencesById = {};
  (day.absences || []).forEach((absence) => {
    if (!absencesById[absence.employee_id]) absencesById[absence.employee_id] = absence;
  });

  const rows = (day.per_employee || [])
    .map((row) => {
      const employee = employeesById[row.employee_id];
      const absence = absencesById[row.employee_id];
      const free = Math.max((row.available || 0) - (row.booked || 0), 0);
      const bookedText = `${formatHours(row.booked, language)}/${formatHours(row.available, language)} h`;
      let text;
      let color;
      let rank;
      // A partial absence is a note under the room row, not the row itself.
      let note = null;
      if (absence && !isAllDayAbsence(absence) && row.available > 0) {
        const config = UNAVAILABILITY_CONFIG[absence.type] || UNAVAILABILITY_CONFIG.personal;
        note = { text: `${t('calendar.month.hover.absent')} ${absenceSpanText(absence)} · ${t(config.labelKey)}`, color: config.color };
      }
      if (absence && !note) {
        const config = UNAVAILABILITY_CONFIG[absence.type] || UNAVAILABILITY_CONFIG.personal;
        text = `${t('calendar.month.hover.absent')} · ${t(config.labelKey)}`;
        color = config.color;
        rank = -2;
      } else if (!(row.available > 0)) {
        text = row.booked > 0 ? bookedText : t('calendar.month.hover.noShift');
        color = row.booked > 0 ? '#c53030' : 'text.disabled';
        rank = row.booked > 0 ? 0 : -1;
      } else if (day.is_past || free <= 0) {
        text = bookedText;
        color = row.booked > row.available ? '#c53030' : 'text.disabled';
        rank = day.is_past ? free : 0;
      } else {
        text = t('calendar.month.cell.freeHours', { hours: formatHours(free, language) });
        color = free >= FREE_HOURS_WORTH_SHOWING ? '#0d9488' : 'text.secondary';
        rank = free;
      }
      return { id: row.employee_id, employee, text, color, rank, note };
    })
    // On a day off "no shift" is true of everyone and says nothing; on a workday it marks
    // the part-timer who is not in that day.
    .filter((row) => day.is_workday || row.rank !== -1)
    .sort((a, b) => b.rank - a.rank);

  const extras = [];
  if (day.unassigned_hours > 0) {
    extras.push({ key: 'unassigned', icon: GroupIcon, text: `${t('calendar.month.hover.unassigned')} · ${formatHours(day.unassigned_hours, language)} h` });
  }
  if (showParts && day.parts_missing > 0) {
    extras.push({ key: 'parts', icon: ErrorOutlineIcon, text: `${t('parts_order.not_ordered_short')} · ${day.parts_missing}` });
  }
  if (showBookings && day.pending_bookings > 0) {
    extras.push({ key: 'bookings', icon: EventAvailableIcon, text: t('calendar.month.cell.pendingBookings', { count: day.pending_bookings }) });
  }
  if (day.note) {
    extras.push({ key: 'note', icon: StickyNote2OutlinedIcon, text: day.notes_count > 1 ? `${day.note} (+${day.notes_count - 1})` : day.note });
  }

  return (
    <Box data-testid={`month-day-hover-${day.date}`} sx={{ p: 1.5, minWidth: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, mb: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
          {t(weekday.shortKey)} {date.getDate()}.{date.getMonth() + 1}.
        </Typography>
        {day.available_hours > 0 && (
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', ...numFont, whiteSpace: 'nowrap' }}>
            {formatHours(day.booked_hours, language)}/{formatHours(day.available_hours, language)} h
          </Typography>
        )}
      </Box>
      {rows.map((row) => (
        <Box key={row.id} data-testid={`month-hover-row-${row.id}`} sx={{ display: 'grid', gridTemplateColumns: '24px minmax(0, 1fr) auto', alignItems: 'center', columnGap: '8px', mb: 0.5 }}>
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 18, borderRadius: '4px', bgcolor: row.employee?.color || '#718096', color: 'white', fontSize: '9px', fontWeight: 700, letterSpacing: '0.02em' }}
          >
            {row.employee?.initials || '?'}
          </Box>
          <Typography sx={{ fontSize: '12px', color: 'text.primary', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.employee?.name || '–'}
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: row.color, lineHeight: 1.4, whiteSpace: 'nowrap', ...numFont }}>
            {row.text}
          </Typography>
          {row.note && (
            <Typography sx={{ gridColumn: '2 / span 2', fontSize: '11px', color: row.note.color, lineHeight: 1.3 }}>
              {row.note.text}
            </Typography>
          )}
        </Box>
      ))}
      {extras.length > 0 && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          {extras.map((extra) => (
            <TooltipRow key={extra.key} icon={extra.icon}>{extra.text}</TooltipRow>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * One day of the month grid: load bar, free room, work-order count and the exceptions that
 * change the picture (parts not ordered, absences, pending bookings, a note).
 */
function MonthDayCell({
  day,
  employeesById,
  selected,
  isShortScreen,
  minHeight = 100,
  showParts,
  showBookings,
  schedulingMode,
  onSelect,
  onOpenDay,
}) {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n?.language);
  const { getStateConfig } = useCalendarColors();
  const [hover, setHover] = React.useState(false);
  const clickTimer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(clickTimer.current), []);

  const handleClick = () => {
    // Scheduling mode has no double-click: the click is the answer, at once.
    if (schedulingMode) {
      onSelect(day);
      return;
    }
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => onSelect(day), DOUBLE_CLICK_GRACE_MS);
  };

  const handleDoubleClick = (event) => {
    event.preventDefault();
    clearTimeout(clickTimer.current);
    onOpenDay(day);
  };

  const dayNumber = Number(day.date.split('-')[2]);
  const quiet = isQuietDay(day);
  const band = loadBand(day);
  // "No capacity" is news on a workday (everyone away, no mechanics) and on a day off with
  // work scheduled on it; a weekend that only carries a note or an online booking request
  // says nothing about capacity (the booking chip already marks the request).
  const showNoCapacity = day.is_workday || day.work_orders > 0;

  if (quiet) {
    return (
      <Box
        data-testid={`month-day-${day.date}`}
        data-quiet="true"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        sx={{
          bgcolor: '#fbfcfd',
          borderRight: 1,
          borderBottom: 1,
          borderColor: '#edf2f7',
          p: '8px 10px',
          minHeight,
          cursor: 'pointer',
          opacity: day.in_month ? 1 : 0.55,
        }}
      >
        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 600, color: 'text.disabled' }}>
          {dayNumber}
        </Typography>
      </Box>
    );
  }

  // What the cell says next to the percentage (the design's table): free hours when there
  // are enough to mention, "ylivaraus" past 100 %, otherwise booked/capacity. A whole free
  // mechanic is the peek panel's detail, the cell keeps to hours like the mock. The hours
  // read teal only on an open day, i.e. when the "does an N h job fit" threshold of the
  // summary tile is met; room below it is real but reads grey, so the eye finds the days
  // that answer the caller's question. A past day says nothing about room at all: its
  // unused hours are not free hours, and the bar and percentage already tell what happened.
  const hasRoom = day.is_open || day.free_hours >= FREE_HOURS_WORTH_SHOWING;
  const freeText = (() => {
    if (day.percent === null || day.percent === undefined || day.is_past) return null;
    if (hasRoom) {
      return t('calendar.month.cell.freeHours', { hours: formatHours(day.free_hours, language) });
    }
    if (day.percent > 100) return t('calendar.month.band.over');
    return `${formatHours(day.booked_hours, language)}/${formatHours(day.available_hours, language)} h`;
  })();

  const freeColor = day.is_open ? '#0d9488' : day.percent > 100 ? '#c53030' : hasRoom ? 'text.secondary' : 'text.disabled';

  return (
    <Tooltip
      title={<DayHoverCard day={day} employeesById={employeesById} showParts={showParts} showBookings={showBookings} />}
      placement="right"
      arrow
      enterDelay={750}
      enterNextDelay={350}
      // Same dress and timing as the event tooltips of the week and day views (DraggableEvent).
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            borderRadius: '10px',
            p: 0,
            maxWidth: 320,
          },
        },
        arrow: { sx: { color: 'background.paper' } },
      }}
    >
      <Box
        data-testid={`month-day-${day.date}`}
        data-percent={day.percent ?? ''}
        data-work-orders={day.work_orders}
        data-open={day.is_open ? 'true' : 'false'}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{
          position: 'relative',
          minHeight,
          // Every column stays the same width whatever a cell holds (the design review caught
          // week 31 widening); long content clips sideways instead of pushing the grid around.
          // Height is never clipped in practice: the row grows to its tallest cell.
          minWidth: 0,
          overflow: 'hidden',
          p: isShortScreen ? '6px 8px 7px' : '8px 10px 9px',
          cursor: schedulingMode ? 'copy' : 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: isShortScreen ? 0.5 : 0.75,
          // A day off keeps the quiet grey even when a note or an absence gives it content,
          // so the work week still reads as a block.
          bgcolor: selected ? '#e8f2fd' : day.is_today ? UI_COLORS.todayBg : hover ? 'background.default' : day.is_workday ? 'background.paper' : '#fbfcfd',
          borderRight: 1,
          borderBottom: 1,
          borderColor: '#edf2f7',
          boxShadow: selected ? 'inset 0 0 0 2px #3182ce' : schedulingMode && hover ? 'inset 0 0 0 2px #805ad5' : 'none',
          opacity: !day.in_month ? 0.55 : day.is_past ? 0.72 : 1,
          transition: 'background-color 140ms',
          userSelect: 'none',
        }}
      >
        {/* date row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {day.is_today ? (
            <Box
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
                color: 'white',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: 700,
              }}
            >
              {dayNumber}
            </Box>
          ) : (
            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, color: day.in_month ? 'text.primary' : 'text.disabled' }}>
              {dayNumber}
            </Typography>
          )}
          {day.work_orders > 0 && (
            <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary' }}>
              {t('calendar.month.cell.workOrders', { count: day.work_orders })}
            </Typography>
          )}
        </Box>

        {/* capacity */}
        {day.percent !== null && day.percent !== undefined ? (
          <Box>
            <LoadBar percent={day.percent} color={band.color} />
            {/* The free text wraps under the percentage when the peek panel narrows the grid,
                so "65%" and "17 h vapaana" never run into each other. */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: '6px', mt: '4px' }}>
              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 700, ...numFont, color: band.text, whiteSpace: 'nowrap' }}>
                {day.percent}%
              </Typography>
              {freeText && (
                <Typography component="span" sx={{ fontSize: '10.5px', fontWeight: day.is_open ? 700 : 600, color: freeColor, whiteSpace: 'nowrap' }}>
                  {freeText}
                </Typography>
              )}
            </Box>
          </Box>
        ) : showNoCapacity ? (
          <Typography component="span" sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
            {t('calendar.month.band.off')}
          </Typography>
        ) : null}

        <Box sx={{ flex: 1 }} />

        {day.work_orders > 0 && <StatusDots byState={day.by_state} getStateConfig={getStateConfig} />}

        {/* exceptions */}
        {(showParts && day.parts_missing > 0) || (day.absences || []).length > 0 || (showBookings && day.pending_bookings > 0) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            {/* The chips explain themselves in the day's hover card; no tooltips of their own. */}
            {showParts && day.parts_missing > 0 && (
              <Box
                component="span"
                data-testid="month-parts-missing"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', px: 0.75, py: '1px', borderRadius: '5px', bgcolor: '#fef3e2', color: '#9c4221', fontSize: '10px', fontWeight: 700 }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 11 }} />
                {day.parts_missing}
              </Box>
            )}
            {showBookings && day.pending_bookings > 0 && (
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', px: 0.75, py: '1px', borderRadius: '5px', bgcolor: BOOKING_STATUS_CONFIG.pending.bg, color: BOOKING_STATUS_CONFIG.pending.text, fontSize: '10px', fontWeight: 700 }}
              >
                <EventAvailableIcon sx={{ fontSize: 11 }} />
                {day.pending_bookings}
              </Box>
            )}
            <AbsenceChips absences={day.absences} employeesById={employeesById} withTooltips={false} />
          </Box>
        ) : null}

        {day.note && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10px',
              color: '#0c4a6e',
              bgcolor: '#e0f2fe',
              px: '5px',
              py: '2px',
              borderRadius: '4px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <StickyNote2OutlinedIcon sx={{ fontSize: 11, flexShrink: 0 }} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{day.note}</Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

// 42 cells re-render on every store change otherwise; the props are stable between payloads.
export default React.memo(MonthDayCell);
